<template>
  <div :data-testid="`${testidPrefix}-editor`" class="pe-root">
    <v-alert v-if="showFixPlate && !disabled" dense text type="warning" class="mb-2">
      {{ $t("companies.periods_normalized", { saved: savedDisplay, fixed: fixedDisplay }) }}
      <v-btn small outlined color="warning" class="ml-2" :data-testid="`${testidPrefix}-accept-fix`"
        @click="acceptFix">
        {{ $t("companies.periods_accept_fix") }}
      </v-btn>
    </v-alert>
    <div ref="track" class="pe-track">
      <template v-for="(seg, i) in segments">
        <div :key="`seg-${i}`" class="pe-segment" :class="{ 'pe-segment--alt': i % 2 === 1 }"
          :style="{ flexGrow: seg.to - seg.from + 1 }" :data-testid="`${testidPrefix}-segment-${i}`">
          <span class="pe-segment__label">{{ segmentLabel(seg) }}</span>
          <v-btn v-if="i > 0 && !disabled" icon x-small class="pe-segment__remove"
            :data-testid="`${testidPrefix}-remove-${i}`" :aria-label="$t('companies.remove_boundary')"
            @click="removeBoundary(i)">
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>
        <div v-if="i < segments.length - 1 && !disabled" :key="`handle-${i + 1}`" class="pe-handle" role="slider"
          tabindex="0" :aria-valuemin="breakpoints[i] + 1" :aria-valuemax="handleMax(i + 1)"
          :aria-valuenow="breakpoints[i + 1]" :data-testid="`${testidPrefix}-handle-${i + 1}`"
          @keydown.left.prevent="moveBoundary(i + 1, -1)" @keydown.right.prevent="moveBoundary(i + 1, 1)"
          @keydown.enter.prevent="startEdit(i + 1)" @click="startEdit(i + 1)"
          @mousedown.prevent="onHandleDown(i + 1, $event)" @touchstart.prevent="onHandleDown(i + 1, $event)"></div>
      </template>
      <div v-if="!disabled" class="pe-handle pe-handle--end" role="slider" tabindex="0"
        :aria-valuemin="breakpoints[breakpoints.length - 1]" :aria-valuemax="max" :aria-valuenow="scaleEnd"
        :data-testid="`${testidPrefix}-end-handle`" :title="$t('companies.scale_end_hint')"
        @keydown.left.prevent="moveScaleEnd(-1)" @keydown.right.prevent="moveScaleEnd(1)"
        @keydown.enter.prevent="startEdit('end')" @click="startEdit('end')"
        @mousedown.prevent="onHandleDown('end', $event)" @touchstart.prevent="onHandleDown('end', $event)"></div>
      <div v-if="scaleEnd < max" class="pe-tail" :style="{ flexGrow: max - scaleEnd }"
        :data-testid="`${testidPrefix}-tail`" :title="$t('companies.periods_tail_hint')">
        <span class="pe-tail__label">{{ scaleEnd + 1 }}–{{ max }}</span>
      </div>
    </div>
    <div class="pe-actions mt-1">
      <v-btn v-if="!disabled" small text color="primary" :disabled="!canAddBoundary"
        :data-testid="`${testidPrefix}-add`" @click="addBoundary">
        <v-icon small class="mr-1">mdi-plus</v-icon>{{ $t("companies.add_boundary") }}
      </v-btn>
      <span class="text-caption text--secondary ml-2">{{ $t("companies.periods_hint") }}</span>
    </div>
    <div v-if="editingIndex !== null" class="pe-edit mt-1">
      <span class="text-caption mr-2">{{ $t("companies.boundary_value") }}</span>
      <input v-model.number="editingValue" type="number" class="pe-edit__input"
        :data-testid="`${testidPrefix}-edit-input`" @keyup.enter="commitEdit" />
      <v-btn small text color="primary" :data-testid="`${testidPrefix}-edit-commit`" @click="commitEdit">
        {{ $t("custom.save") }}
      </v-btn>
      <v-btn v-if="editingIndex !== 'end' && editingIndex > 0" small text color="error"
        :data-testid="`${testidPrefix}-edit-remove`" @click="removeEditedBoundary">
        {{ $t("companies.remove_boundary") }}
      </v-btn>
      <v-btn small text @click="cancelEdit">{{ $t("custom.close") }}</v-btn>
    </div>
  </div>
</template>

<script>
import {
  parsePeriods,
  serializePeriods,
  segmentsFromBreakpoints,
} from "./periodsModel";

export default {
  name: "PeriodsEditor",
  props: {
    value: { type: Array, default: null },
    min: { type: Number, required: true },
    max: { type: Number, required: true },
    disabled: { type: Boolean, default: false },
    testidPrefix: { type: String, default: "periods" },
  },
  data() {
    return {
      breakpoints: [this.min],
      scaleEnd: this.max,
      showFixPlate: false,
      editingIndex: null,
      editingValue: null,
      dragIndex: null,
    };
  },
  computed: {
    savedDisplay() {
      return Array.isArray(this.value) && this.value.length ? this.value.join(", ") : "—";
    },
    fixedDisplay() {
      return serializePeriods(this.breakpoints, this.scaleEnd).join(", ");
    },
    segments() {
      return segmentsFromBreakpoints(this.breakpoints, this.scaleEnd);
    },
    canAddBoundary() {
      return this.segments.some((s) => s.to > s.from);
    },
  },
  watch: {
    // Не гейтим по mounted: value может прийти позже гидрации Vuex.
    value: {
      immediate: true,
      handler(raw) {
        const { breakpoints, scaleEnd, normalized } = parsePeriods(raw, this.min, this.max);
        this.breakpoints = breakpoints;
        this.scaleEnd = scaleEnd;
        this.showFixPlate = normalized;
      },
    },
  },
  beforeDestroy() {
    this.unbindDrag();
  },
  methods: {
    segmentLabel(seg) {
      return seg.from === seg.to ? `${seg.from}` : `${seg.from}–${seg.to}`;
    },
    handleMax(i) {
      return (i + 1 < this.breakpoints.length ? this.breakpoints[i + 1] : this.scaleEnd + 1) - 1;
    },
    clampScaleEnd(val) {
      return Math.min(Math.max(val, this.breakpoints[this.breakpoints.length - 1]), this.max);
    },
    moveScaleEnd(delta) {
      const next = this.clampScaleEnd(this.scaleEnd + delta);
      if (next === this.scaleEnd) return;
      this.scaleEnd = next;
      this.emitChange();
    },
    emitChange() {
      this.showFixPlate = false;
      this.$emit("input", serializePeriods(this.breakpoints, this.scaleEnd));
    },
    acceptFix() {
      this.emitChange();
    },
    moveBoundary(i, delta) {
      const next = this.clampBoundary(i, this.breakpoints[i] + delta);
      if (next === this.breakpoints[i]) return;
      this.$set(this.breakpoints, i, next);
      this.emitChange();
    },
    clampBoundary(i, val) {
      const lo = this.breakpoints[i - 1] + 1;
      const hi = this.handleMax(i);
      return Math.min(Math.max(val, lo), hi);
    },
    addBoundary() {
      let idx = 0;
      let len = 0;
      this.segments.forEach((s, i) => {
        const l = s.to - s.from + 1;
        if (l > len) {
          len = l;
          idx = i;
        }
      });
      if (len < 2) return;
      const seg = this.segments[idx];
      this.breakpoints.splice(idx + 1, 0, seg.from + Math.floor(len / 2));
      this.emitChange();
    },
    removeBoundary(i) {
      if (i < 1) return;
      this.breakpoints.splice(i, 1);
      this.emitChange();
    },
    removeEditedBoundary() {
      const i = this.editingIndex;
      this.cancelEdit();
      if (i !== "end") this.removeBoundary(i);
    },
    startEdit(i) {
      this.editingIndex = i;
      this.editingValue = i === "end" ? this.scaleEnd : this.breakpoints[i];
    },
    commitEdit() {
      const i = this.editingIndex;
      if (i === null) return;
      if (Number.isInteger(this.editingValue)) {
        if (i === "end") {
          this.scaleEnd = this.clampScaleEnd(this.editingValue);
        } else {
          const next = this.clampBoundary(i, this.editingValue);
          if (next !== this.breakpoints[i]) {
            this.$set(this.breakpoints, i, next);
          }
        }
        this.emitChange();
      }
      this.cancelEdit();
    },
    cancelEdit() {
      this.editingIndex = null;
      this.editingValue = null;
    },
    onHandleDown(i, event) {
      this.dragIndex = i;
      this.dragMoved = false;
      document.addEventListener("mousemove", this.onDragMove);
      document.addEventListener("touchmove", this.onDragMove, { passive: false });
      document.addEventListener("mouseup", this.onDragEnd);
      document.addEventListener("touchend", this.onDragEnd);
      event.stopPropagation();
    },
    onDragMove(event) {
      if (this.dragIndex === null || !this.$refs.track) return;
      if (event.cancelable) event.preventDefault();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const rect = this.$refs.track.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      const raw = this.min + Math.round(ratio * (this.max - this.min));
      if (this.dragIndex === "end") {
        const val = this.clampScaleEnd(raw);
        if (val !== this.scaleEnd) {
          this.scaleEnd = val;
          this.dragMoved = true;
        }
        return;
      }
      const val = this.clampBoundary(this.dragIndex, raw);
      if (val !== this.breakpoints[this.dragIndex]) {
        this.$set(this.breakpoints, this.dragIndex, val);
        this.dragMoved = true;
      }
    },
    onDragEnd() {
      const moved = this.dragMoved;
      this.unbindDrag();
      if (moved) this.emitChange();
    },
    unbindDrag() {
      this.dragIndex = null;
      this.dragMoved = false;
      document.removeEventListener("mousemove", this.onDragMove);
      document.removeEventListener("touchmove", this.onDragMove);
      document.removeEventListener("mouseup", this.onDragEnd);
      document.removeEventListener("touchend", this.onDragEnd);
    },
  },
};
</script>

<style scoped>
.pe-track {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 44px;
  border: 1px solid rgba(128, 128, 128, 0.45);
  border-radius: 6px;
  overflow: hidden;
}
.pe-segment {
  flex-basis: 0;
  min-width: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: rgba(33, 150, 243, 0.05);
}
.pe-segment--alt {
  background: rgba(33, 150, 243, 0.18);
}
.pe-segment__label {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}
.pe-segment__remove {
  position: absolute;
  top: 1px;
  right: 1px;
  opacity: 0.6;
  z-index: 3; /* выше хит-зоны ручки — крестик кликабелен */
}
.pe-handle {
  flex: 0 0 6px;
  background: rgba(128, 128, 128, 0.8);
  cursor: col-resize;
  position: relative;
  touch-action: none;
  z-index: 1;
}
.pe-handle--end {
  background: rgba(33, 150, 243, 0.8);
}
.pe-handle::after {
  /* Расширенная хит-зона для тача (REQ-08); верх свободен под «×» */
  content: "";
  position: absolute;
  top: 24px;
  bottom: 0;
  left: -14px;
  right: -14px;
}
.pe-tail {
  flex-basis: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: repeating-linear-gradient(135deg, rgba(128, 128, 128, 0.12), rgba(128, 128, 128, 0.12) 6px, transparent 6px, transparent 12px);
}
.pe-tail__label {
  font-size: 12px;
  opacity: 0.5;
  white-space: nowrap;
}
.pe-handle:focus {
  outline: none;
  background: #2196f3;
}
/* Мобильная вёрстка: «×» из сегментов уходит (удаление — в строке
   редактирования границы), ручки толще под палец, хит-зона на всю высоту. */
@media (max-width: 599px) {
  .pe-segment__remove {
    display: none;
  }
  .pe-segment {
    min-width: 22px;
  }
  .pe-segment__label {
    font-size: 12px;
  }
  .pe-handle {
    flex-basis: 12px;
  }
  .pe-handle::after {
    top: 0;
    left: -10px;
    right: -10px;
  }
  .pe-track {
    min-height: 52px;
  }
  .pe-tail__label {
    display: none;
  }
}
.pe-edit__input {
  width: 72px;
  padding: 2px 6px;
  border: 1px solid rgba(128, 128, 128, 0.6);
  border-radius: 4px;
  color: inherit;
}
</style>
